/**
 * Proxy URL Utility
 * 
 * Handles URL rewriting for development proxy to bypass CORS.
 * In production, returns the original URL.
 * In development, routes through Vite's proxy server.
 */

const isDevelopment = import.meta.env.DEV;

// Proxy mappings for development
const PROXY_MAPPINGS: Record<string, string> = {
  // R2 bucket URL -> proxy path
  'https://pub-2b88453828ec4b51a61ddb78045021ab.r2.dev': '/api/r2',
  // ATC CDN URL -> proxy path
  'https://cdn.atc.chillairwaves.com': '/api/atc',
};

/**
 * Rewrites external URLs to go through the dev proxy in development mode.
 * In production, returns the original URL unchanged.
 * 
 * @param url - The original URL to potentially rewrite
 * @returns The proxied URL in development, or original URL in production
 */
export function getProxiedUrl(url: string): string {
  if (!isDevelopment) {
    return url;
  }

  // Check if the URL matches any of our proxy mappings
  for (const [externalBase, proxyPath] of Object.entries(PROXY_MAPPINGS)) {
    if (url.startsWith(externalBase)) {
      const relativePath = url.slice(externalBase.length);
      return `${proxyPath}${relativePath}`;
    }
  }

  // No proxy mapping found, return original URL
  return url;
}

/**
 * Gets the base URL for R2 bucket (proxied in dev, direct in prod)
 */
export function getR2BaseUrl(): string {
  const bucketUrl = import.meta.env.VITE_R2_MUSIC_BUCKET_URL || 'https://pub-2b88453828ec4b51a61ddb78045021ab.r2.dev';
  
  if (isDevelopment) {
    return '/api/r2';
  }
  
  return bucketUrl;
}

/**
 * Gets the base URL for ATC CDN (proxied in dev, direct in prod)
 */
export function getAtcCdnUrl(): string {
  const cdnUrl = import.meta.env.VITE_ATC_CDN_URL || 'https://cdn.atc.chillairwaves.com';
  
  if (isDevelopment) {
    return '/api/atc';
  }
  
  return cdnUrl;
}
